import { motion } from 'framer-motion';
import { TrendingUp, Users, Phone, Target, Zap, BarChart3 } from 'lucide-react';
import { useInView } from '../../hooks/useAnimations';

const DashboardShowcase = () => {
  const [ref, isInView] = useInView({ threshold: 0.1 });

  const recentLeads = [
    { name: 'Sarah Johnson', company: 'TechCorp', status: 'Hot', time: '2m ago' },
    { name: 'Mike Chen', company: 'StartupXYZ', status: 'Warm', time: '15m ago' },
    { name: 'Emily Davis', company: 'Enterprise Inc', status: 'New', time: '32m ago' },
    { name: 'Alex Wilson', company: 'Growth Co', status: 'Hot', time: '1h ago' },
  ];

  const telecallers = [
    { name: 'Priya S.', calls: 45, conversions: 12, avatar: 'PS' },
    { name: 'Rahul M.', calls: 38, conversions: 9, avatar: 'RM' },
    { name: 'Anjali P.', calls: 52, conversions: 15, avatar: 'AP' },
    { name: 'Vikram K.', calls: 41, conversions: 11, avatar: 'VK' },
  ];

  const stats = [
    { label: 'Total Leads', value: '2,456', change: '+12%', icon: Users, color: 'from-purple-500 to-indigo-500' },
    { label: 'Calls Today', value: '342', change: '+8%', icon: Phone, color: 'from-blue-500 to-cyan-500' },
    { label: 'Conversions', value: '89', change: '+23%', icon: Target, color: 'from-green-500 to-emerald-500' },
    { label: 'Performance', value: '94%', change: '+5%', icon: BarChart3, color: 'from-orange-500 to-amber-500' },
  ];

  return (
    <section ref={ref} className="py-24 relative overflow-hidden bg-gradient-to-b from-white via-slate-50 to-white">
      {/* Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity }}
          className="absolute top-1/2 left-1/4 w-[500px] h-[500px] rounded-full orb-purple"
        />
        <motion.div
          animate={{ scale: [1.2, 1, 1.2], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 10, repeat: Infinity }}
          className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full orb-indigo"
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 mb-6"
          >
            <Zap className="w-4 h-4 text-purple-600" />
            <span className="text-sm font-medium text-purple-700">Real-Time Dashboard</span>
          </motion.div>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>
            Powerful Dashboard,{' '}
            <span className="text-gradient">Real Results</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Everything you need to manage your team and track performance in one intuitive interface.
          </p>
        </motion.div>

        {/* Stats Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
        >
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3 + index * 0.1 }}
              whileHover={{ y: -4 }}
              className="bg-white rounded-2xl p-6 border border-purple-100 shadow-lg shadow-purple-500/5 hover:shadow-xl hover:shadow-purple-500/10 transition-all"
            >
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center mb-4`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
              <div className="text-2xl md:text-3xl font-bold text-slate-900 mb-1">{stat.value}</div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">{stat.label}</span>
                <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full">{stat.change}</span>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Dashboard Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Recent Leads */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-2xl p-6 border border-purple-100 shadow-lg shadow-purple-500/5"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-slate-900">Recent Leads</h3>
              <span className="flex items-center gap-2 px-3 py-1 rounded-full bg-green-50 text-green-600 text-xs font-medium">
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                Live
              </span>
            </div>
            <div className="space-y-3">
              {recentLeads.map((lead, i) => (
                <motion.div
                  key={lead.name}
                  initial={{ opacity: 0, x: -10 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.5 + i * 0.1 }}
                  className="flex items-center justify-between p-3 rounded-xl bg-slate-50 hover:bg-purple-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-100 to-indigo-100 flex items-center justify-center text-sm font-medium text-purple-700">
                      {lead.name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">{lead.name}</p>
                      <p className="text-xs text-slate-500">{lead.company}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                      lead.status === 'Hot' ? 'bg-purple-100 text-purple-700' :
                      lead.status === 'Warm' ? 'bg-amber-100 text-amber-700' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {lead.status}
                    </span>
                    <p className="text-xs text-slate-400 mt-1">{lead.time}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Top Performers */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.5 }}
            className="bg-white rounded-2xl p-6 border border-purple-100 shadow-lg shadow-purple-500/5"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-slate-900">Top Telecallers</h3>
              <span className="text-xs text-slate-500 font-medium">Today</span>
            </div>
            <div className="space-y-3">
              {telecallers.map((caller, i) => (
                <motion.div
                  key={caller.name}
                  initial={{ opacity: 0, x: 10 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.6 + i * 0.1 }}
                  className="flex items-center justify-between p-3 rounded-xl bg-slate-50 hover:bg-purple-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center text-sm font-medium text-white">
                      {caller.avatar}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">{caller.name}</p>
                      <p className="text-xs text-slate-500">Top Performer</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <p className="text-sm font-semibold text-slate-900">{caller.calls}</p>
                      <p className="text-xs text-slate-400">Calls</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-semibold text-purple-600">{caller.conversions}</p>
                      <p className="text-xs text-slate-400">Conv.</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Performance Chart */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.7 }}
          className="mt-6 bg-white rounded-2xl p-6 border border-purple-100 shadow-lg shadow-purple-500/5"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Performance Overview</h3>
              <p className="text-sm text-slate-500">Last 7 days performance metrics</p>
            </div>
            <div className="flex gap-4">
              {['Calls', 'Leads', 'Revenue'].map((item, i) => (
                <div key={item} className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${['bg-purple-500', 'bg-purple-400', 'bg-purple-300'][i]}`} />
                  <span className="text-sm text-slate-600">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Chart */}
          <div className="relative h-48">
            <div className="absolute inset-0 flex items-end justify-between px-2 gap-3">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, i) => (
                <div key={day} className="flex-1 flex flex-col items-center gap-1">
                  <div className="w-full flex gap-1 items-end h-36">
                    <motion.div
                      initial={{ height: 0 }}
                      animate={isInView ? { height: `${[65, 45, 80, 55, 90, 70, 85][i]}%` } : {}}
                      transition={{ delay: 0.8 + i * 0.1, duration: 0.5 }}
                      className="flex-1 bg-gradient-to-t from-purple-600 to-purple-400 rounded-t"
                    />
                    <motion.div
                      initial={{ height: 0 }}
                      animate={isInView ? { height: `${[50, 60, 70, 45, 80, 55, 75][i]}%` } : {}}
                      transition={{ delay: 0.9 + i * 0.1, duration: 0.5 }}
                      className="flex-1 bg-gradient-to-t from-purple-400 to-purple-300 rounded-t"
                    />
                    <motion.div
                      initial={{ height: 0 }}
                      animate={isInView ? { height: `${[40, 55, 60, 70, 75, 65, 80][i]}%` } : {}}
                      transition={{ delay: 1 + i * 0.1, duration: 0.5 }}
                      className="flex-1 bg-gradient-to-t from-purple-300 to-purple-200 rounded-t"
                    />
                  </div>
                  <span className="text-xs text-slate-500">{day}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default DashboardShowcase;